import { Alert, Box, Button } from "@mui/joy";
import { toPng } from "html-to-image";
import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { buildMusicianCardLink } from "../hooks/qrCard/musicianCardLink";
import { useInstruments } from "../redux/instrument/instrumentHooks";
import { useMyMember } from "../redux/member/memberHooks";

// 1×1 transparent PNG. Used as a stand-in for any image html-to-image can't
// inline (e.g. an avatar on a bucket without CORS headers) so it substitutes
// a blank pixel instead of tainting the canvas and aborting the whole export.
const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+M8AAAMBAQDJ/pLvAAAAAElFTkSuQmCC";

/**
 * Load a (possibly cross-origin) image into a same-origin data URL so it can
 * be embedded when the card is rasterized. Resolves to `null` when the host
 * doesn't send CORS headers (the canvas would be tainted) — the caller then
 * keeps showing the raw URL, which is fine for on-screen display.
 *
 * @param {string} src the image URL
 * @returns {Promise<string | null>} a data URL, or null when CORS-blocked
 */
function loadImageAsDataUrl(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d")?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * The musician's personal QR card — the web counterpart of the rhapsody-mobile
 * "My QR Code" sheet. Encodes a universal link that lets a contractor add this
 * musician to their address book (in-app if installed, otherwise via the web
 * fallback). The card can be shared / downloaded as a PNG (e.g. for a resume).
 *
 * @returns {ReactElement} the QR card page
 */
export function QrCode(): ReactElement {
  const { member } = useMyMember();
  const { instrumentsMap } = useInstruments();
  const cardRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // CORS-safe copy of the avatar for the export. Falls back to the raw URL for
  // display when the host blocks CORS (see loadImageAsDataUrl).
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);

  // Email / phone live in the member's contacts (top-level email is a
  // convenience copy); fall back to it when there's no EMAIL contact.
  const phone = member?.contacts?.find(
    (c) => c.category === "PHONE" && c.phone
  )?.phone;
  const emailFromContacts = member?.contacts?.find(
    (c) => c.category === "EMAIL" && c.email
  )?.email;
  const email = member?.email || emailFromContacts;

  // Instrument names come from the catalog (member rows carry only ids);
  // fall back to a row's instrumentName when present.
  const instruments = useMemo(() => {
    const names = (member?.memberInstruments ?? []).map(
      (mi) => instrumentsMap?.[mi.instrumentID]?.name || mi.instrumentName || ""
    );
    return Array.from(new Set(names.map((n) => n.trim()).filter(Boolean)));
  }, [member, instrumentsMap]);

  const fullName =
    [member?.firstName, member?.middleName, member?.lastName]
      .filter((p) => p && p.trim())
      .join(" ")
      .trim() ||
    member?.nickName ||
    member?.companyName ||
    "";

  const link = useMemo(
    () =>
      buildMusicianCardLink({
        firstName: member?.firstName,
        middleName: member?.middleName,
        lastName: member?.lastName,
        nickName: member?.nickName,
        companyName: member?.companyName,
        email,
        phone,
        avatar: member?.avatar,
        instruments,
      }),
    [member, email, phone, instruments]
  );

  const initials = `${member?.firstName?.[0] ?? ""}${
    member?.lastName?.[0] ?? ""
  }`.toUpperCase();

  // Pre-resolve the avatar to a same-origin data URL so it survives the PNG
  // export. If the host doesn't allow CORS we keep showing the raw URL (it
  // still renders on screen) and the export falls back to a blank pixel.
  useEffect(() => {
    const src = member?.avatar;
    if (!src) {
      setAvatarDataUrl(null);
      return;
    }
    let cancelled = false;
    loadImageAsDataUrl(src).then((url) => {
      if (!cancelled) setAvatarDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [member?.avatar]);

  const avatarSrc = avatarDataUrl ?? member?.avatar;

  const download = (dataUrl: string) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "rhapsody-card.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleShare = async () => {
    if (!cardRef.current) return;
    setError(null);
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 3,
        cacheBust: true,
        imagePlaceholder: TRANSPARENT_PIXEL,
        // Don't try to inline remote @font-face stylesheets (Google Fonts,
        // Material Icons, Typekit). Their cross-origin cssRules can't be read,
        // which floods the console with caught SecurityErrors and slows the
        // export. The card pins its own system font stack (see below), so the
        // rasterized text renders correctly without any embedded fonts.
        skipFonts: true,
      });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "rhapsody-card.png", { type: "image/png" });
      const nav = navigator as Navigator & {
        canShare?: (data: { files: File[] }) => boolean;
        share?: (data: { files: File[]; title?: string }) => Promise<void>;
      };
      if (nav.canShare?.({ files: [file] }) && nav.share) {
        try {
          await nav.share({ files: [file], title: "My Rhapsody QR Card" });
          return;
        } catch (shareErr) {
          // The user dismissing the share sheet isn't an error.
          if ((shareErr as Error)?.name === "AbortError") return;
          // Anything else (e.g. desktop browsers that advertise file sharing
          // but reject it): fall through to a plain download.
        }
      }
      download(dataUrl);
    } catch (e) {
      setError("Couldn't generate the card. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        p: 2,
        width: "100%",
      }}
    >
      {/* Exportable card — inline styles so it captures deterministically. */}
      <div
        ref={cardRef}
        style={{
          width: "100%",
          maxWidth: 360,
          background: "linear-gradient(135deg, #0D0F1F, #15335B)",
          borderRadius: 20,
          padding: 28,
          boxSizing: "border-box",
          textAlign: "center",
          color: "#ffffff",
          // Explicit stack so the PNG export renders text consistently without
          // relying on remote web fonts (we skip font inlining on export).
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        }}
      >
        <img
          src="/logo_main-white.png"
          alt="Rhapsody"
          style={{ height: 26, objectFit: "contain" }}
        />
        <div
          style={{ marginTop: 20, display: "flex", justifyContent: "center" }}
        >
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt=""
              style={{
                width: 72,
                height: 72,
                borderRadius: 18,
                objectFit: "cover",
              }}
            />
          ) : (
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 18,
                background: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 26,
                fontWeight: 700,
              }}
            >
              {initials}
            </div>
          )}
        </div>
        <div style={{ marginTop: 12, fontSize: 22, fontWeight: 800 }}>
          {fullName}
        </div>
        {member?.companyName ? (
          <div style={{ marginTop: 2, color: "rgba(255,255,255,0.7)" }}>
            {member.companyName}
          </div>
        ) : null}
        {instruments.length ? (
          <div
            style={{
              marginTop: 6,
              color: "#ffffff",
              fontWeight: 600,
              letterSpacing: 0.2,
            }}
          >
            {instruments.join(" · ")}
          </div>
        ) : null}
        <div
          style={{
            marginTop: 22,
            background: "#ffffff",
            borderRadius: 18,
            padding: 16,
            display: "inline-block",
          }}
        >
          <QRCode value={link} size={196} />
          <div
            style={{
              marginTop: 10,
              color: "#9e9e9e",
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: 0.2,
            }}
          >
            Scan to add to your Address Book
          </div>
        </div>
        {email ? (
          <div style={{ marginTop: 16, color: "#ffffff" }}>{email}</div>
        ) : null}
        {phone ? (
          <div style={{ marginTop: 4, color: "#ffffff" }}>{phone}</div>
        ) : null}
      </div>

      <Button
        onClick={handleShare}
        loading={busy}
        startDecorator={<i className="fa-solid fa-arrow-up-from-bracket" />}
        sx={{ borderRadius: 999, px: 3 }}
      >
        Share
      </Button>

      {error ? (
        <Alert color="danger" variant="soft">
          {error}
        </Alert>
      ) : null}
    </Box>
  );
}
